import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiCall } from '../utils/apiService';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import logger from '../utils/logger';
import { useDebounce } from '../hooks/useDebounce';
import UsersSection from './AdminDashboard/UsersSection';
import ConsultantsSection from './AdminDashboard/ConsultantsSection';
import TransactionsSection from './AdminDashboard/TransactionsSection';

const AdminDashboard = () => {
  const { isAuthenticated, user, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('users');

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [usersPagination, setUsersPagination] = useState({ page: 1, per_page: 20, total: 0, total_pages: 0 });
  const [usersSearch, setUsersSearch] = useState('');
  const debouncedUsersSearch = useDebounce(usersSearch, 500);
  const [usersRoleFilter, setUsersRoleFilter] = useState('user');
  const [usersStatusFilter, setUsersStatusFilter] = useState('all');

  // Consultants state
  const [consultants, setConsultants] = useState([]);
  const [consultantsLoading, setConsultantsLoading] = useState(false);
  const [consultantsError, setConsultantsError] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [consultantsPagination, setConsultantsPagination] = useState({ page: 1, per_page: 20, total: 0, total_pages: 0 });
  const [consultantsSearch, setConsultantsSearch] = useState('');
  const debouncedConsultantsSearch = useDebounce(consultantsSearch, 500);
  const [consultantsStatusFilter, setConsultantsStatusFilter] = useState('all');

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState('');
  const [transactionsPagination, setTransactionsPagination] = useState({ page: 1, per_page: 20, total: 0, total_pages: 0 });
  const [transactionsSearch, setTransactionsSearch] = useState('');
  const debouncedTransactionsSearch = useDebounce(transactionsSearch, 500);
  const [transactionsStatusFilter, setTransactionsStatusFilter] = useState('all');
  const [transactionsCurrencyFilter, setTransactionsCurrencyFilter] = useState('all');

  // Consultant assignment states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningConsultant, setAssigningConsultant] = useState(false);
  const [selectedConsultantId, setSelectedConsultantId] = useState('');
  const assignModalRef = useRef(null);
  
  // Assignment requests state
  const [assignmentRequests, setAssignmentRequests] = useState([]);
  const [assignmentRequestsLoading, setAssignmentRequestsLoading] = useState(false);

  // Counts state for tabs (loaded separately for initial render)
  const [tabCounts, setTabCounts] = useState({
    users: 0,
    consultants: 0,
    transactions: 0
  });

  const [initialLoad, setInitialLoad] = useState(true);
  
  // Refs to track if we're resetting pagination (to prevent loading with wrong page)
  const isResettingUsersPagination = useRef(false);
  const isResettingConsultantsPagination = useRef(false);
  const isResettingTransactionsPagination = useRef(false);

  // Load counts on initial mount (for tab badges)
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    
    // Load transaction count
    const loadTransactionCount = async () => {
      try {
        const result = await apiCall('GET', `/admin/payments?page=1&per_page=1`);
        if (result.success && result.pagination) {
          setTabCounts(prev => ({ ...prev, transactions: result.pagination.total || 0 }));
        }
      } catch (err) {
        // Silently handle timeouts for count loading - not critical
        if (!(err.isNetworkError && err.message?.includes('timeout'))) {
          logger.error('Failed to load transaction count:', err);
        }
      }
    };
    
    // Load consultant count
    const loadConsultantCount = async () => {
      try {
        const result = await apiCall('GET', `/auth/consultants?page=1&per_page=1`);
        if (result.success && result.pagination) {
          setTabCounts(prev => ({ ...prev, consultants: result.pagination.total || 0 }));
        }
      } catch (err) {
        // Silently handle timeouts for count loading - not critical
        if (!(err.isNetworkError && err.message?.includes('timeout'))) {
          logger.error('Failed to load consultant count:', err);
        }
      }
    };
    
    // Load both counts in parallel (non-blocking)
    Promise.all([loadTransactionCount(), loadConsultantCount()]).catch(() => {
      // Silently handle any errors - counts are not critical for initial load
    });
  }, [user]);

  // Initial load - only on mount or when auth changes
  // Preload all tab data for faster switching
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    
    // Load initial tab data with skeleton
    if (activeTab === 'users') {
      loadUsers(true);
      loadConsultants(true);
      loadAssignmentRequests(true);
    } else if (activeTab === 'consultants') {
      loadConsultants(true);
    } else if (activeTab === 'transactions') {
      loadTransactions(true);
    }
    
    // Preload other tabs in background (without showing loading)
    // This makes tab switching instant
    // Use setTimeout to avoid blocking the initial tab load
    setTimeout(() => {
      if (activeTab !== 'consultants') {
        loadConsultants(false);
      }
      if (activeTab !== 'transactions') {
        loadTransactions(false);
      }
    }, 100);
    
    setInitialLoad(false);
  }, [user]); // Only depend on user, not activeTab

  // Reload when tab changes (show skeleton on first switch, silent reload after)
  useEffect(() => {
    if (initialLoad || !user || user.role !== 'admin') return;
    
    if (activeTab === 'users') {
      loadUsers(false);
      loadConsultants(false);
      loadAssignmentRequests(false);
    } else if (activeTab === 'consultants') {
      // Only show loading if consultants haven't been loaded yet
      const showLoading = consultants.length === 0;
      loadConsultants(showLoading);
    } else if (activeTab === 'transactions') {
      // Only show loading if transactions haven't been loaded yet
      const showLoading = transactions.length === 0;
      loadTransactions(showLoading);
    }
  }, [activeTab]);

  // Reset pagination to page 1 when search or filters change (but not when page changes)
  // This ensures that when user searches/filters, they start from page 1 of filtered results
  // Also clear any existing errors immediately when filters change to prevent showing stale errors
  // Use debounced search to avoid triggering on every keystroke
  useEffect(() => {
    if (initialLoad || !user || user.role !== 'admin') return;
    
    if (activeTab === 'users') {
      // Always clear errors immediately when search/filters change (prevents stale error messages)
      setUsersError('');
      // Reset to page 1 when search or filters change (but only if we're not already on page 1)
      if (usersPagination.page !== 1) {
        isResettingUsersPagination.current = true;
        setUsersPagination(prev => ({ ...prev, page: 1 }));
        // Load data after reset completes (with page 1)
        setTimeout(() => {
          isResettingUsersPagination.current = false;
          loadUsers(false);
        }, 50);
      } else {
        // Already on page 1, just load with new filters (the reload useEffect will handle this)
        // But we skip it here to avoid double loading
      }
    }
  }, [debouncedUsersSearch, usersRoleFilter, usersStatusFilter, activeTab, initialLoad, user]);

  useEffect(() => {
    if (initialLoad || !user || user.role !== 'admin') return;
    
    if (activeTab === 'consultants') {
      // Always clear errors immediately when search/filters change (prevents stale error messages)
      setConsultantsError('');
      // Reset to page 1 when search or filters change
      if (consultantsPagination.page !== 1) {
        isResettingConsultantsPagination.current = true;
        setConsultantsPagination(prev => ({ ...prev, page: 1 }));
        // Load data after reset completes (with page 1)
        setTimeout(() => {
          isResettingConsultantsPagination.current = false;
          loadConsultants(false);
        }, 50);
      }
    }
  }, [debouncedConsultantsSearch, consultantsStatusFilter, activeTab, initialLoad, user]);

  useEffect(() => {
    if (initialLoad || !user || user.role !== 'admin') return;
    
    if (activeTab === 'transactions') {
      // Always clear errors immediately when search/filters change (prevents stale error messages)
      setTransactionsError('');
      // Reset to page 1 when search or filters change
      if (transactionsPagination.page !== 1) {
        isResettingTransactionsPagination.current = true;
        // Reset pagination to page 1
        setTransactionsPagination(prev => ({ ...prev, page: 1 }));
        isResettingTransactionsPagination.current = true;
        
        // Load data after state update completes
        // Use the latest state values from dependency array (not closure)
        setTimeout(() => {
          loadTransactions(false, 1, debouncedTransactionsSearch, transactionsStatusFilter, transactionsCurrencyFilter);
          isResettingTransactionsPagination.current = false;
        }, 100); // Delay to ensure pagination state update completes
      } else {
        // Already on page 1, but filters changed - load with page 1 explicitly
        // Use the values from the dependency array (latest state) not from closure
        // The dependency array ensures we get the latest values when this effect runs
        setTimeout(() => {
          // Use the latest state values directly (they're in the dependency array)
          loadTransactions(false, 1, debouncedTransactionsSearch, transactionsStatusFilter, transactionsCurrencyFilter);
        }, 50);
      }
    }
  }, [debouncedTransactionsSearch, transactionsStatusFilter, transactionsCurrencyFilter, activeTab, initialLoad, user]);

  // Reload when filters/search/page change (but don't show loading spinner)
  // Skip loading if we're in the middle of resetting pagination
  // Use a ref to track the last loaded state to prevent duplicate loads
  const lastUsersStateRef = useRef({ page: 1, search: '', role: '', status: '' });
  const lastConsultantsStateRef = useRef({ page: 1, search: '', status: '' });
  const lastTransactionsStateRef = useRef({ page: 1, search: '', status: '', currency: '' });

  useEffect(() => {
    if (initialLoad || !user || user.role !== 'admin') return;
    
    if (activeTab === 'users') {
      // Skip if we're resetting pagination (will load after reset completes)
      if (isResettingUsersPagination.current) {
        return;
      }
      
      // Check if state actually changed to prevent unnecessary loads
      // Use debounced search for API calls
      const currentState = {
        page: usersPagination.page,
        search: debouncedUsersSearch,
        role: usersRoleFilter,
        status: usersStatusFilter
      };
      
      const lastState = lastUsersStateRef.current;
      if (
        currentState.page === lastState.page &&
        currentState.search === lastState.search &&
        currentState.role === lastState.role &&
        currentState.status === lastState.status
      ) {
        return; // No change, skip load
      }
      
      // Only load if page is 1 or if page changed (not if only filters changed - that's handled by reset useEffect)
      if (currentState.page === 1 || currentState.page !== lastState.page) {
        lastUsersStateRef.current = currentState;
        loadUsers(false);
      }
    } else if (activeTab === 'consultants') {
      // Skip if we're resetting pagination (will load after reset completes)
      if (isResettingConsultantsPagination.current) {
        return;
      }
      
      // Check if state actually changed
      // Use debounced search for API calls
      const currentState = {
        page: consultantsPagination.page,
        search: debouncedConsultantsSearch,
        status: consultantsStatusFilter
      };
      
      const lastState = lastConsultantsStateRef.current;
      if (
        currentState.page === lastState.page &&
        currentState.search === lastState.search &&
        currentState.status === lastState.status
      ) {
        return; // No change, skip load
      }
      
      // Only load if page is 1 or if page changed
      if (currentState.page === 1 || currentState.page !== lastState.page) {
        lastConsultantsStateRef.current = currentState;
        loadConsultants(false);
      }
    } else if (activeTab === 'transactions') {
      // Skip if we're resetting pagination (will load after reset completes)
      if (isResettingTransactionsPagination.current) {
        return;
      }
      
      // Check if state actually changed
      // Use debounced search for API calls
      const currentState = {
        page: transactionsPagination.page,
        search: debouncedTransactionsSearch,
        status: transactionsStatusFilter,
        currency: transactionsCurrencyFilter
      };
      
      const lastState = lastTransactionsStateRef.current;
      
      // Check if filters changed (including search being cleared)
      const filtersChanged = (
        currentState.search !== lastState.search ||
        currentState.status !== lastState.status ||
        currentState.currency !== lastState.currency
      );
      
      // If nothing changed, skip
      if (
        !filtersChanged &&
        currentState.page === lastState.page
      ) {
        return; // No change, skip load
      }
      
      // If filters changed, we need to reload
      // If page changed, we need to reload
      // Always update the ref first to prevent duplicate loads
      lastTransactionsStateRef.current = currentState;
      
      // If filters changed and we're not on page 1, the filter change useEffect will handle it
      // Otherwise, load with current state
      if (filtersChanged && currentState.page !== 1) {
        // Filter change useEffect will handle this, skip here
        return;
      }
      
      // Load with current state (filters changed on page 1, or page changed)
      loadTransactions(false, null, currentState.search, currentState.status, currentState.currency);
    }
  }, [usersPagination.page, debouncedUsersSearch, usersRoleFilter, usersStatusFilter,
    consultantsPagination.page, debouncedConsultantsSearch, consultantsStatusFilter,
    transactionsPagination.page, debouncedTransactionsSearch,
    transactionsStatusFilter, transactionsCurrencyFilter]);

  // Poll for assignment requests when users tab is active
  useEffect(() => {
    if (!user || user.role !== 'admin' || activeTab !== 'users') return;
    
    // Load immediately when tab becomes active
    loadAssignmentRequests(false);
    
    // Poll every 5 seconds for new assignment requests (silent polling)
    const pollInterval = setInterval(() => {
      loadAssignmentRequests(false);
    }, 5000);
    
    // Cleanup interval on unmount or tab change
    return () => {
      clearInterval(pollInterval);
    };
  }, [activeTab, user]);

  const loadUsers = async (showLoading = true) => {
    try {
      if (showLoading) {
        setUsersLoading(true);
      }
      setUsersError('');

      const params = new URLSearchParams({
        page: usersPagination.page.toString(),
        per_page: usersPagination.per_page.toString(),
        sort_by: 'created_at',
        sort_order: 'desc'
      });

      if (debouncedUsersSearch) params.append('search', debouncedUsersSearch);
      if (usersRoleFilter !== 'all') params.append('role', usersRoleFilter);
      if (usersStatusFilter !== 'all') params.append('status', usersStatusFilter);

      const result = await apiCall('GET', `/admin/users?${params.toString()}`);

      if (result.success) {
        setUsers(result.data || []);
        setUsersPagination(prev => ({ ...prev, ...result.pagination }));
        // Update user count for tab badge
        if (result.pagination) {
          setTabCounts(prev => ({ ...prev, users: result.pagination.total || 0 }));
        }
        // Clear any previous errors on successful load
        setUsersError('');
      } else {
        setUsersError('Failed to load users');
      }
    } catch (err) {
      // Handle network errors gracefully - only set error if we don't have existing data
      if (err.isNetworkError) {
        if (err.message?.includes('timeout')) {
          logger.debug('Users load timeout - will retry on next action');
          // Only show error if we don't have existing users data
          if (users.length === 0) {
            setUsersError('Request timed out. Please try again.');
          }
        } else if (err.isContentLengthMismatch) {
          // Content length mismatch is often transient - silently retry
          logger.debug('Users load content length mismatch - will retry on next action');
          // Only show error if we don't have existing users data
          if (users.length === 0) {
            setUsersError('Network issue. Please refresh or try again.');
          }
        } else {
          logger.debug('Users load network error - will retry on next action');
          // Only show error if we don't have existing users data
          if (users.length === 0) {
            setUsersError('Network error. Please try again.');
          }
        }
      } else {
        logger.error('Failed to load users:', err);
        // Only set error if we don't have existing users data
        if (users.length === 0) {
          setUsersError('Failed to load users');
        }
      }
    } finally {
      if (showLoading) {
        setUsersLoading(false);
      }
    }
  };

  const loadAssignmentRequests = async (showLoading = false) => {
    try {
      if (showLoading) {
        setAssignmentRequestsLoading(true);
      }
      const result = await apiCall('GET', `/admin/consultant-assignment-requests?status=pending`);
      
      if (result.success) {
        const newRequests = result.requests || [];
        // Only update if the data actually changed to avoid unnecessary re-renders
        setAssignmentRequests(prevRequests => {
          // Compare by length and IDs to detect changes
          if (prevRequests.length !== newRequests.length) {
            logger.debug(`🔄 Assignment requests updated: ${prevRequests.length} → ${newRequests.length}`);
            return newRequests;
          }
          // Check if any request IDs changed
          const prevIds = new Set(prevRequests.map(r => r.id));
          const newIds = new Set(newRequests.map(r => r.id));
          if (prevIds.size !== newIds.size || [...prevIds].some(id => !newIds.has(id))) {
            logger.debug(`🔄 Assignment requests changed`);
            return newRequests;
          }
          return prevRequests; // No change, return previous to avoid re-render
        });
      }
    } catch (err) {
      // Handle timeout errors gracefully - don't spam console
      if (err.isNetworkError && err.message?.includes('timeout')) {
        logger.debug('Assignment requests load timeout - will retry on next action');
      } else {
        logger.error('Failed to load assignment requests:', err);
      }
    } finally {
      if (showLoading) {
        setAssignmentRequestsLoading(false);
      }
    }
  };

  const loadConsultants = async (showLoading = true) => {
    try {
      if (showLoading) {
        setConsultantsLoading(true);
      }
      setConsultantsError('');

      const params = new URLSearchParams({
        page: consultantsPagination.page.toString(),
        per_page: consultantsPagination.per_page.toString(),
        sort_by: 'created_at',
        sort_order: 'desc'
      });

      if (debouncedConsultantsSearch) params.append('search', debouncedConsultantsSearch);
      if (consultantsStatusFilter !== 'all') params.append('status', consultantsStatusFilter);

      const result = await apiCall('GET', `/auth/consultants?${params.toString()}`);

      if (result.success) {
        setConsultants(result.data || []);
        setConsultantsPagination(prev => ({ ...prev, ...result.pagination }));
        // Update consultant count for tab badge
        if (result.pagination) {
          setTabCounts(prev => ({ ...prev, consultants: result.pagination.total || 0 }));
        }
      } else {
        setConsultantsError('Failed to load consultants');
      }
    } catch (err) {
      // Handle timeout errors gracefully
      if (err.isNetworkError && err.message?.includes('timeout')) {
        logger.debug('Consultants load timeout - will retry on next action');
        setConsultantsError('Request timed out. Please try again.');
      } else {
        logger.error('Failed to load consultants:', err);
        setConsultantsError('Failed to load consultants');
      }
    } finally {
      if (showLoading) {
        setConsultantsLoading(false);
      }
    }
  };

  const handleAssignConsultant = async (userId, consultantId, requestId = null) => {
    try {
      setAssigningConsultant(true);
      
      // If this is from a pending request, resolve it first
      if (requestId && consultantId) {
        const resolveResult = await apiCall('POST', `/admin/consultant-assignment-request/${requestId}/resolve`, {
          consultant_id: consultantId,
          notes: 'Assigned via admin dashboard'
        });
        
        if (!resolveResult.success) {
          toast.error(resolveResult.message || 'Failed to resolve assignment request');
          return;
        }
      }
      
      // Then assign the consultant to user (always do this)
      const result = await apiCall('POST', `/admin/users/${userId}/assign-consultant`, {
        consultant_id: consultantId || ''
      });
      
      if (result.success) {
        toast.success(consultantId ? 'Consultant assigned successfully' : 'Consultant unassigned successfully');
        setShowAssignModal(false);
        setSelectedUser(null);
        setSelectedConsultantId('');
        loadUsers(false);
        loadAssignmentRequests(false); // Refresh requests list
      } else {
        toast.error(result.message || 'Failed to assign consultant');
      }
    } catch (error) {
      logger.error('Failed to assign consultant:', error);
      toast.error('Failed to assign consultant');
    } finally {
      setAssigningConsultant(false);
    }
  };

  const loadTransactions = async (showLoading = true, forcePage = null, searchOverride = null, statusOverride = null, currencyOverride = null) => {
    try {
      if (showLoading) {
        setTransactionsLoading(true);
      }
      setTransactionsError('');

      // Use override values if provided, otherwise use debounced search
      // This ensures we always use the latest values, not stale closure values
      const currentSearch = searchOverride !== null ? searchOverride : debouncedTransactionsSearch;
      const currentStatus = statusOverride !== null ? statusOverride : transactionsStatusFilter;
      const currentCurrency = currencyOverride !== null ? currencyOverride : transactionsCurrencyFilter;
      
      // Determine page to use - prefer forcePage, then check reset flag, then use current page
      const pageToUse = forcePage !== null 
        ? forcePage 
        : (isResettingTransactionsPagination.current ? 1 : transactionsPagination.page);
      
      const perPage = transactionsPagination.per_page;

      const params = new URLSearchParams({
        page: pageToUse.toString(),
        per_page: perPage.toString(),
        sort_by: 'created_at',
        sort_order: 'desc'
      });

      if (currentSearch) params.append('search', currentSearch);
      if (currentStatus !== 'all') params.append('status', currentStatus);
      if (currentCurrency !== 'all') params.append('currency', currentCurrency);

      const result = await apiCall('GET', `/admin/payments?${params.toString()}`);

      if (result.success) {
        setTransactions(result.data || []);
        setTransactionsPagination(prev => ({ ...prev, ...result.pagination }));
        // Update transaction count for tab badge
        if (result.pagination) {
          setTabCounts(prev => ({ ...prev, transactions: result.pagination.total || 0 }));
        }
        // Clear any errors on successful load
        setTransactionsError('');
      } else {
        // Only set error if we don't have existing data (to prevent showing error when data exists)
        if (transactions.length === 0) {
          setTransactionsError('Failed to load transactions');
        } else {
          logger.warn('Failed to load transactions, but keeping existing data');
        }
      }
    } catch (err) {
      // Handle timeout errors gracefully
      if (err.isNetworkError && err.message?.includes('timeout')) {
        logger.debug('Transactions load timeout - will retry on next action');
        // Only set error if we don't have existing data
        if (transactions.length === 0) {
          setTransactionsError('Request timed out. Please try again.');
        }
      } else {
        logger.error('Failed to load transactions:', err);
        // Only set error if we don't have existing data
        if (transactions.length === 0) {
          setTransactionsError('Failed to load transactions');
        }
      }
    } finally {
      if (showLoading) {
        setTransactionsLoading(false);
      }
    }
  };

  const handleApproval = async (userId, status, adminNotes = '') => {
    try {
      setActionLoading(prev => ({ ...prev, [userId]: true }));
      setConsultantsError('');

      const result = await apiCall('POST', `/auth/consultants/${userId}/approve`, {
        user_id: userId,
        status: status,
        admin_notes: adminNotes
      });

      if (result.success) {
        await loadConsultants(false);
      } else {
        setConsultantsError(`Failed to ${status} consultant`);
      }
    } catch (err) {
      setConsultantsError(`Failed to ${status} consultant`);
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', label: 'Pending' },
      approved: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', label: 'Approved' },
      rejected: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', label: 'Rejected' },
      completed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', label: 'Completed' },
      failed: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', label: 'Failed' },
      refunded: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-300', label: 'Refunded' }
    };

    const statusConfig = config[status] || config.pending;

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
        {statusConfig.label}
      </span>
    );
  };

  // Update counts when data changes
  useEffect(() => {
    setTabCounts(prev => ({
      ...prev,
      users: usersPagination.total > 0 ? usersPagination.total : prev.users,
      consultants: consultantsPagination.total > 0 ? consultantsPagination.total : prev.consultants,
      transactions: transactionsPagination.total > 0 ? transactionsPagination.total : prev.transactions
    }));
  }, [usersPagination.total, consultantsPagination.total, transactionsPagination.total]);

  // Tab configuration
  const tabs = [
    { id: 'users', name: 'Users', icon: '👥', count: tabCounts.users },
    { id: 'consultants', name: 'Consultants', icon: '👨‍💼', count: tabCounts.consultants },
    { id: 'transactions', name: 'Transactions', icon: '💳', count: tabCounts.transactions }
  ];

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate">
              Admin Dashboard
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Welcome back, {user.name}
            </p>
          </div>

          {/* Admin Dropdown */}
          {isAuthenticated && hasRole('admin') && (
            <div className="relative group">
              <button className="w-full md:w-auto text-gray-700 dark:text-gray-300 hover:text-emerald-700 dark:hover:text-emerald-400 px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center justify-between md:justify-start bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700">
                <span className="flex items-center">
                  <span className="mr-2">⚙️</span>
                  Admin Menu
                </span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute right-0 mt-2 w-full md:w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border border-gray-200 dark:border-gray-700">
                <div className="py-1">
                  <Link to="/admin/questions" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-emerald-700 dark:hover:text-emerald-400">
                    📋 Manage Questions
                  </Link>
                  <Link to="/admin/industry" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-emerald-700 dark:hover:text-emerald-400">
                    🏭 Manage Industries
                  </Link>
                  <Link to="/admin/subindustry" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-emerald-700 dark:hover:text-emerald-400">
                    🔗 Manage Sub-Industries
                  </Link>
                  <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>
                  <Link to="/conversion-funnel" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-emerald-700 dark:hover:text-emerald-400">
                    📊 Analytics Dashboard
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <nav className="-mb-px flex space-x-4 md:space-x-8 px-4 md:px-6 min-w-max md:min-w-0" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                    ${activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <span className="mr-2 text-lg">{tab.icon}</span>
                  {tab.name}
                  {tab.count > 0 && (
                    <span className={`
                      ml-2 md:ml-3 py-0.5 px-2 md:px-2.5 rounded-full text-xs font-medium
                      ${activeTab === tab.id
                        ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-300'
                      }
                    `}>
                      {tab.count}
                    </span>
                  )}
                  {/* Show pending requests badge for Users tab */}
                  {tab.id === 'users' && assignmentRequests.length > 0 && (
                    <span className="ml-2 py-0.5 px-2 rounded-full text-xs font-medium bg-blue-500 dark:bg-blue-600 text-white animate-pulse">
                      {assignmentRequests.length} pending
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'users' && (
              <UsersSection
                users={users}
                loading={usersLoading}
                error={usersError}
                pagination={usersPagination}
                setPagination={setUsersPagination}
                search={usersSearch}
                setSearch={setUsersSearch}
                roleFilter={usersRoleFilter}
                setRoleFilter={setUsersRoleFilter}
                statusFilter={usersStatusFilter}
                setStatusFilter={setUsersStatusFilter}
                loadUsers={loadUsers}
                getStatusBadge={getStatusBadge}
                setSelectedUser={setSelectedUser}
                setShowAssignModal={setShowAssignModal}
                setSelectedConsultantId={setSelectedConsultantId}
                assignmentRequests={assignmentRequests}
                assignmentRequestsLoading={assignmentRequestsLoading}
                handleAssignConsultant={handleAssignConsultant}
                consultants={consultants}
              />
            )}

            {activeTab === 'consultants' && (
              <ConsultantsSection
                consultants={consultants}
                loading={consultantsLoading}
                error={consultantsError}
                handleApproval={handleApproval}
                getStatusBadge={getStatusBadge}
                actionLoading={actionLoading}
                loadConsultants={loadConsultants}
                pagination={consultantsPagination}
                setPagination={setConsultantsPagination}
                search={consultantsSearch}
                setSearch={setConsultantsSearch}
                statusFilter={consultantsStatusFilter}
                setStatusFilter={setConsultantsStatusFilter}
              />
            )}

            {activeTab === 'transactions' && (
              <TransactionsSection
                transactions={transactions}
                loading={transactionsLoading}
                error={transactionsError}
                pagination={transactionsPagination}
                setPagination={setTransactionsPagination}
                search={transactionsSearch}
                setSearch={setTransactionsSearch}
                statusFilter={transactionsStatusFilter}
                setStatusFilter={setTransactionsStatusFilter}
                currencyFilter={transactionsCurrencyFilter}
                setCurrencyFilter={setTransactionsCurrencyFilter}
                loadTransactions={loadTransactions}
                getStatusBadge={getStatusBadge}
              />
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {activeTab === 'consultants' && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-md flex items-center justify-center">
                      <span className="text-yellow-600 dark:text-yellow-400 text-sm">⏳</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Pending Approval
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {consultants.filter(c => c.consultant_status === 'pending').length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-md flex items-center justify-center">
                      <span className="text-green-600 dark:text-green-400 text-sm">✓</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Approved
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {consultants.filter(c => c.consultant_status === 'approved').length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-md flex items-center justify-center">
                      <span className="text-red-600 dark:text-red-400 text-sm">❌</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Rejected
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {consultants.filter(c => c.consultant_status === 'rejected').length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Consultant Assignment Modal */}
      {showAssignModal && selectedUser && (
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Close modal when clicking outside (on backdrop)
            if (e.target === e.currentTarget) {
              setShowAssignModal(false);
              setSelectedUser(null);
              setSelectedConsultantId('');
            }
          }}
        >
          <div 
            ref={assignModalRef}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Assign Consultant</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Assign a consultant to <span className="font-medium">{selectedUser.name}</span> for live assessment sessions
              </p>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Consultant
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={selectedConsultantId}
                onChange={(e) => setSelectedConsultantId(e.target.value)}
                disabled={assigningConsultant}
              >
                <option value="">No consultant (Remove assignment)</option>
                {consultants.map(consultant => (
                  <option key={consultant.id} value={consultant.id}>
                    {consultant.name} ({consultant.email})
                  </option>
                ))}
              </select>
              {consultants.length === 0 && (
                <p className="mt-2 text-sm text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 p-2 rounded">
                  ⚠️ No approved consultants available. Please approve consultants in the Consultants tab first.
                </p>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => { 
                  setShowAssignModal(false); 
                  setSelectedUser(null);
                  setSelectedConsultantId('');
                }}
                disabled={assigningConsultant}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleAssignConsultant(selectedUser.id, selectedConsultantId, selectedUser.requestId)}
                disabled={assigningConsultant}
                className="px-4 py-2 bg-emerald-600 dark:bg-emerald-700 border border-transparent rounded-md text-sm font-medium text-white hover:bg-emerald-700 dark:hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {assigningConsultant ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Assigning...
                  </>
                ) : (
                  selectedConsultantId ? 'Assign Consultant' : 'Remove Assignment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default AdminDashboard;
